// Assessment stage resolution & shuffling helpers

export const ELEMENT_TO_LETTER = { fire: 'A', water: 'B', wind: 'C', earth: 'D' };
export const ELEMENT_TO_STAGE2_KEY = { fire: '2A', water: '2B', wind: '2C', earth: '2D' };

// Fisher-Yates shuffle with deterministic seed
export const shuffleArray = (array, seed) => {
  const shuffled = [...array];
  let currentIndex = shuffled.length;
  const seededRandom = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };
  let seedValue = seed;
  while (currentIndex !== 0) {
    seedValue++;
    const randomIndex = Math.floor(seededRandom(seedValue) * currentIndex);
    currentIndex--;
    [shuffled[currentIndex], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[currentIndex]];
  }
  return shuffled;
};

const generateGenericStage = (stage, prevElement, scenarioId) => {
  const continuations = {
    2: `Your ${prevElement} response has created a new dynamic in the situation. How do you proceed?`,
    3: `As the situation reaches its conclusion, what is your final approach?`
  };
  return {
    code: `${scenarioId}.${stage}.generic`,
    stem: continuations[stage] || "How do you respond?",
    options: {
      fire: { text: "You take decisive action to move things forward quickly.", score: { fire: 1, water: 0, wind: 0, earth: 0 } },
      water: { text: "You focus on the emotional and relational aspects of the situation.", score: { fire: 0, water: 1, wind: 0, earth: 0 } },
      wind: { text: "You step back to gain perspective and consider alternative approaches.", score: { fire: 0, water: 0, wind: 1, earth: 0 } },
      earth: { text: "You establish clear structure and process to bring stability.", score: { fire: 0, water: 0, wind: 0, earth: 1 } }
    }
  };
};

const findStage1Choice = (choices, scenarioId) => choices.find(c => c.scenario_id === scenarioId && c.stage === 1);
const findStage2Choice = (choices, scenarioId) => choices.find(c => c.scenario_id === scenarioId && c.stage === 2);

export const getStageData = (scenario, stage, previousChoices) => {
  if (!scenario) return null;

  if (stage === 1) {
    return scenario.stages["1"] || scenario.stages.stage_1;
  }

  if (stage === 2) {
    const prevChoice = findStage1Choice(previousChoices, scenario.id);
    if (!prevChoice) return null;
    const stage2Key = ELEMENT_TO_STAGE2_KEY[prevChoice.element];
    let stage2Data = scenario.stages[stage2Key];
    if (!stage2Data) {
      stage2Data = scenario.stages.stage_2?.find(s => s.trigger_prev === prevChoice.element);
    }
    if (!stage2Data) {
      stage2Data = generateGenericStage(2, prevChoice.element, scenario.id);
    }
    return stage2Data;
  }

  if (stage === 3) {
    const stage1Choice = findStage1Choice(previousChoices, scenario.id);
    const stage2Choice = findStage2Choice(previousChoices, scenario.id);
    if (!stage1Choice || !stage2Choice) return null;
    const pathLetter = ELEMENT_TO_LETTER[stage1Choice.element];
    const stage2Letter = ELEMENT_TO_LETTER[stage2Choice.element];
    const stage3Key = `3${pathLetter}${stage2Letter}`;
    let stage3Data = scenario.stages[stage3Key];
    if (!stage3Data) {
      stage3Data = scenario.stages.stage_3?.find(s => s.parent_code === stage2Choice.code && s.trigger_prev === stage2Choice.element);
    }
    if (!stage3Data) {
      stage3Data = generateGenericStage(3, stage2Choice.element, scenario.id);
    }
    return stage3Data;
  }

  return null;
};

export const buildShuffledOptions = (stageData, currentScenario, currentStage) => {
  if (!stageData?.options) return [];
  const seedString = `${currentScenario?.id}-${currentStage}-${stageData.code || stageData.id || ''}`;
  const seed = seedString.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);

  let optionsArray;
  if (Array.isArray(stageData.options)) {
    optionsArray = stageData.options.map(opt => [opt.element, opt]);
  } else {
    optionsArray = Object.entries(stageData.options);
  }
  return shuffleArray(optionsArray, seed);
};

export const resolveOptionData = (stageData, element) => {
  if (Array.isArray(stageData.options)) {
    return stageData.options.find(opt => opt.element === element);
  }
  return stageData.options[element];
};

export const defaultScoreForElement = (element) => ({
  fire: element === 'fire' ? 1 : 0,
  water: element === 'water' ? 1 : 0,
  wind: element === 'wind' ? 1 : 0,
  earth: element === 'earth' ? 1 : 0
});
